import { Flex } from '@aloft/toolkit-sui';

import React from 'react';
import { Root, createRoot } from 'react-dom/client';

import { MAP_PIN_STATUS_FILTERS, PinStatusFilter } from './constants.ts';

type StatusCheckboxesProps = {
  selected: PinStatusFilter[];
  onChange: (statuses: PinStatusFilter[]) => void;
  variant?: 'sales' | 'rental';
};

const FILTERS_SALES: PinStatusFilter[] = [
  MAP_PIN_STATUS_FILTERS['SELECTED COMPS'],
  MAP_PIN_STATUS_FILTERS.ACTIVE,
  MAP_PIN_STATUS_FILTERS.SOLD,
  MAP_PIN_STATUS_FILTERS.PENDING,
];

const statusFilterLabel = (status: PinStatusFilter, variant: 'sales' | 'rental'): string => {
  if (variant === 'rental') {
    if (status === MAP_PIN_STATUS_FILTERS.SOLD) {
      return 'Rented';
    }
    if (status === MAP_PIN_STATUS_FILTERS.ACTIVE) {
      return 'Active';
    }
    if (status === MAP_PIN_STATUS_FILTERS.PENDING) {
      return 'Pending';
    }
    if (status === MAP_PIN_STATUS_FILTERS['SELECTED COMPS']) {
      return 'Selected comps';
    }
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

// eslint-disable-next-line react-refresh/only-export-components
const StatusCheckboxes: React.FC<StatusCheckboxesProps> = ({
  selected,
  onChange,
  variant = 'sales',
}) => {
  const toggleStatus = (status: PinStatusFilter) => {
    const updated = selected.includes(status)
      ? selected.filter((s) => s !== status)
      : [...selected, status];
    onChange(updated);
  };

  const filters =
    variant === 'rental' ? [...FILTERS_SALES, MAP_PIN_STATUS_FILTERS.OTHER] : FILTERS_SALES;

  return (
    <Flex padding={1} gap={1} flexDirection="column" flexWrap="wrap">
      {filters.map((status) => (
        <label
          htmlFor={status}
          key={status}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          {/* eslint-disable-next-line aloft/prefer-sound-ui-checkbox */}
          <input
            type="checkbox"
            id={status}
            checked={selected.includes(status)}
            onChange={() => toggleStatus(status)}
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              cursor: 'pointer',
              position: 'relative',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          />
          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
            {statusFilterLabel(status, variant)}
          </span>
        </label>
      ))}
    </Flex>
  );
};

type StatusFilterControlProps = StatusCheckboxesProps;

export class StatusFilterControl {
  private container: HTMLDivElement;
  private reactRoot?: Root;
  private props: StatusFilterControlProps;

  constructor(props: StatusFilterControlProps) {
    this.props = props;
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
  }

  onAdd(): HTMLDivElement {
    this.reactRoot = createRoot(this.container);
    this.reactRoot.render(<StatusCheckboxes {...this.props} />);
    return this.container;
  }

  onRemove(): void {
    this.reactRoot?.unmount();
    this.container.remove();
  }

  updateProps(newProps: StatusFilterControlProps) {
    this.props = newProps;
    this.reactRoot?.render(<StatusCheckboxes {...this.props} />);
  }
}
